import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Chip,
  Popover,
} from '@mui/material';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';
import {
  Activity,
  ActivitySheet,
  Appointment,
  GroupAppointment,
  updateAppointmentVisitNote,
} from '../../services/configApi/activitySheet/weeklyActivitySheetProvider';
import { FC, useContext, useState } from 'react';
import ActivitySheetVisitView, { VisitNote } from './ActivitySheetVisitView';
import MediumTypography from '../../components/formlib/MediumTypography';
import { getAppStartTime } from '../../utils/dateUtil';
import ActivityNoteModal from '../../components/formlib/modal/ActivityNoteModal';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import { ResponseType } from '../../utils/type';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { ActivitySheetContext } from '../../context/ActivitySheetContext';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { ReactComponent as ChevronUp } from '../../assets/images/chevronUp.svg';
import { ReactComponent as ChevronDown } from '../../assets/images/chevronDown.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/infoIcon.svg';
import SelectComponent from '../../components/formlib/SelectComponent';
import { StatusType } from './ActivityTableRow';
import ModalPopup from '../../components/formlib/ModalPopup';
import dayjs from 'dayjs';

interface Header {
  day: string;
  clientName: string;
  apptStartTime: string;
  duration: string;
  serviceCode: string;
  settingCode: string;
  location: string;
  modifiers: string;
  status: string;
  actions: string;
}

const headCells: HeadCell<Header>[] = [
  {
    id: 'day',
    labelId: 'dayText',
    defaultLabelId: 'Day',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'clientName',
    labelId: 'Dashboard.ClientName',
    defaultLabelId: 'Client Name',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'apptStartTime',
    labelId: 'appt',
    defaultLabelId: 'Appt Start Time',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'duration',
    labelId: 'myTimeOffDuration',
    defaultLabelId: 'Duration',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'serviceCode',
    labelId: 'serviceCodeText',
    defaultLabelId: 'Service Code',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'settingCode',
    labelId: 'settingCodeText',
    defaultLabelId: 'Setting Code',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'location',
    labelId: 'locationText',
    defaultLabelId: 'Location',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'modifiers',
    labelId: 'modifiersText',
    defaultLabelId: 'Modifiers',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'status',
    labelId: 'statusText',
    defaultLabelId: 'Status',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'actions',
    labelId: 'actions',
    defaultLabelId: 'Actions',
    numeric: false,
    requiredSorting: false,
  },
];

interface BillableHoursViewProps {
  activityDetails?: ActivitySheet;
  selectedDate?: Date;
}

interface AppointmentRowProps {
  activitySheetId: string;
  appointment: Appointment;
  isGroupBilling: boolean;
  index: number;
  removeBorder: boolean;
  hasInterpreter: boolean;
  parentArchived: boolean;
  parentStatus: StatusType | null;
  selectedDate?: Date;
  onUpdateVisitNote: (note: VisitNote, activitySheetId: string) => void;
}

const AppointmentRow: FC<AppointmentRowProps> = ({
  activitySheetId,
  appointment,
  isGroupBilling,
  index,
  removeBorder,
  hasInterpreter,
  parentArchived,
  parentStatus,
  onUpdateVisitNote,
  selectedDate,
}) => {
  return (
    <ActivitySheetVisitView
      activitySheetId={activitySheetId}
      key={appointment.appointmentId}
      appointment={appointment}
      isGroupBilling={isGroupBilling}
      index={index}
      removeBorder={removeBorder}
      hasInterpreter={hasInterpreter}
      rowId={index}
      parentArchived={parentArchived}
      parentStatus={parentStatus}
      selectedDate={selectedDate}
      onUpdateVisitNote={(note) => onUpdateVisitNote(note, activitySheetId)}
    />
  );
};

interface GroupBillingRowProps {
  activity: Activity;
  selectedDate?: Date;
  groupAppointment: GroupAppointment;
  onUpdateVisitNote: (note: VisitNote, activitySheetId: string) => void;
}

const GroupBillingRow: FC<GroupBillingRowProps> = ({
  activity,
  selectedDate,
  groupAppointment,
  onUpdateVisitNote,
}) => {
  return (
    <>
      {groupAppointment.appointments.map((appt: Appointment, index: number) => {
        let removeBorder = false;
        if (
          groupAppointment.isGroupBilling &&
          !groupAppointment.hasInterpreter &&
          groupAppointment.appointments.length - 1 !== index
        ) {
          removeBorder = true;
        }
        return (
          <AppointmentRow
            key={appt.appointmentId}
            activitySheetId={activity.activitySheetId}
            appointment={appt}
            isGroupBilling={groupAppointment.isGroupBilling}
            index={index}
            removeBorder={removeBorder}
            hasInterpreter={groupAppointment.hasInterpreter}
            parentArchived={activity.archived}
            parentStatus={activity.status}
            selectedDate={selectedDate}
            onUpdateVisitNote={onUpdateVisitNote}
          />
        );
      })}
    </>
  );
};

const BillableHoursView: FC<BillableHoursViewProps> = ({
  activityDetails,
  selectedDate,
}) => {
  const [noteModal, setNoteModal] = useState<{
    activitySheetId: string;
    progressNotesId: string;
    visitId: string;
    note: string;
    open: boolean;
  }>({
    activitySheetId: '',
    progressNotesId: '',
    visitId: '',
    note: '',
    open: false,
  });
  const [toastrId, setToastrId] = useState<string>();
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [successOrError, setSuccessOrError] = useState<ResponseType>('success');
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const { context, canBillingHoursEdit } = useContext(ActivitySheetContext);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [showConfirmationPopup, setShowConfirmationPopup] =
    useState<boolean>(false);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const getWeekendBackgroundColor = (
    day: string | null,
    isHoliday: boolean | null,
  ) => {
    if (dayjs(day).day() === 0 || dayjs(day).day() === 6) {
      return 'var(--thom-green-primary2-bg)';
    }
    return isHoliday ? '#FFEBDD' : 'white';
  };

  const setUpdateVisitNote = (note: VisitNote, activitySheetId: string) => {
    setNoteModal({
      activitySheetId: activitySheetId,
      progressNotesId: note.progressNotesId,
      visitId: note.visitId,
      note: note.note,
      open: true,
    });
  };

  const isAllActivitiesVisitsReviewed = () => {
    return !(activityDetails?.activities ?? []).some((activity) =>
      (activity.groupBilling ?? []).some((gb) =>
        gb.appointments.some(
          (app) => app.status === 'Submitted' && !app.onHold,
        ),
      ),
    );
  };

  const isAllDayLevelReviewed = () => {
    for (const activity of activityDetails?.activities ?? []) {
      if (activity.status === 'Submitted') return false;
    }
    return true;
  };

  const updateVisitLevelNote = (
    activitySheetId: string,
    progressNotesId: string,
    visitId: string,
    description: string,
  ) => {
    if (visitId && progressNotesId !== null) {
      toggleLoader(true);
      updateAppointmentVisitNote({
        activitySheetId: activitySheetId,
        visitId: visitId,
        description,
      })
        .then(() => {
          if (context) {
            context.updateVisitLevelNote(progressNotesId, visitId, description);
          }
          setNoteModal({
            open: false,
            progressNotesId: '',
            activitySheetId: '',
            visitId: '',
            note: '',
          });
          if (context) {
            context.updateNoteErrorMessage(
              'success',
              'visitNoteUpdateSuccessMessage',
              'Visit note updated successfully',
            );
          }
          toggleLoader(false);
        })
        .catch((error) => {
          if (context) {
            if (isCustomError(error)) {
              const apiError = error as ApiError;
              context.updateNoteErrorMessage(
                'error',
                apiError.id,
                apiError.message,
              );
            } else {
              context.updateNoteErrorMessage(
                'error',
                'visitNoteUpdateFailureMessage',
                'Failed to update visit note',
              );
            }
          }
          toggleLoader(false);
        });
    }
  };

  const canHideSaveButton = () => {
    if (canBillingHoursEdit) {
      if (
        context?.activityStatus === null ||
        context?.activityStatus === 'Not Submitted' ||
        context?.activityStatus === 'Rejected'
      ) {
        return true;
      }
      return false;
    }
    return true;
  };

  const canShowSubmitActions =
    canBillingHoursEdit &&
    checkPermissionForFeature('backend.billing_hours_ras', 'editPermission');
  const showSaveAndSubmit =
    canShowSubmitActions &&
    context?.activityStatus !== null &&
    context?.activityStatus !== 'Rejected' &&
    context?.activityStatus !== 'Not Submitted';

  // Add this helper function before readDisabledItems
  const hasRejectedVisits = (activity: Activity) => {
    // Check direct appointments
    if (activity.appointments?.some((appt) => appt.status === 'Rejected')) {
      return true;
    }

    // Check group billing appointments
    if (
      activity.groupBilling?.some((gb) =>
        gb.appointments.some((appt) => appt.status === 'Rejected'),
      )
    ) {
      return true;
    }

    return false;
  };

  // Modify readDisabledItems function
  const readDisabledItems = (activity: Activity) => {
    const items: string[] = ['Pending'];
    if (hasRejectedVisits(activity)) {
      items.push('Closed');
    }
    return items;
  };

  return (
    <>
      {toastrId && (
        <SnackBarComponent
          open={toastrId !== undefined}
          handleClose={() => {
            setToastrId(undefined);
          }}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
          successOrError={successOrError}
        />
      )}
      {showConfirmationPopup && (
        <ModalPopup
          open={showConfirmationPopup}
          description="billableHoursSubmissionWarningMessage"
          onCancel={() => setShowConfirmationPopup(false)}
          onOk={() => {
            context?.submitReviewedBillableHours?.();
            setShowConfirmationPopup(false);
          }}
          labelId1="Clientpage.cancelbtn"
          negativeActionLabel="Cancel"
          labelId2="Clientpage.Okbtn"
          positiveActionLabel="Ok"
        />
      )}
      <TableContainer className="tableStyles" sx={{ overflowX: 'unset' }}>
        <Table>
          <TableHeader
            className="listDataTableHead"
            headerNames={headCells}
            checkBoxRequired={false}
          />
          <TableBody className="tableRowcss">
            {activityDetails?.activities?.map((activity) => {
              return (
                <>
                  <TableRow
                    sx={{
                      backgroundColor: getWeekendBackgroundColor(
                        activity.day,
                        activity.isHoliday,
                      ),
                    }}
                  >
                    <TableCell colSpan={headCells.length - 2}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {activity.groupBilling?.length > 0 ? (
                          <Box
                            className="pr-xs cursorPointer"
                            onClick={() => {
                              context?.onChevronClick?.(
                                !activity.expand,
                                activity.activitySheetId,
                              );
                            }}
                          >
                            {activity.expand && <ChevronUp />}
                            {!activity.expand && <ChevronDown />}
                          </Box>
                        ) : (
                          <Box className="pr-md" />
                        )}
                        <MediumTypography
                          label={getAppStartTime(
                            activity.day,
                            'dddd, MMM DD',
                            true,
                          )}
                        />

                        {activity.holidayTitle && (
                          <Chip
                            size="small"
                            label={activity.holidayTitle}
                            sx={{
                              fontFamily: 'Lato-Regular',
                              fontSize: '10px',
                              color: 'white',
                              backgroundColor: '#F96300',
                              marginLeft: '8px',
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {context?.activityStatus !== 'Not Submitted' && (
                        <SelectComponent
                          disabled={
                            !canBillingHoursEdit ||
                            context?.activityStatus === 'Rejected'
                          }
                          customSx={{
                            height: '28px',
                            width: '100px',
                          }}
                          disableItems={readDisabledItems(activity)}
                          names={['Open', 'Closed', 'Pending']}
                          value={activity.activityStatus ?? 'Pending'}
                          handleChange={(value: string) => {
                            context?.updateActivityStatus?.(
                              activity.activitySheetId,
                              value as 'Open' | 'Closed' | 'Pending',
                            );
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell className="paddingZero"></TableCell>
                  </TableRow>
                  {activity.expand &&
                    activity.groupBilling?.map((groupAppointment) => (
                      <GroupBillingRow
                        key={`${activity.activitySheetId}-${groupAppointment.appointments[0]?.appointmentId}`}
                        activity={activity}
                        selectedDate={selectedDate}
                        groupAppointment={groupAppointment}
                        onUpdateVisitNote={setUpdateVisitNote}
                      />
                    ))}
                </>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {noteModal.open && (
        <ActivityNoteModal
          open={noteModal.open}
          note={noteModal.note ?? ''}
          handleClose={() =>
            setNoteModal({
              ...noteModal,
              open: false,
            })
          }
          maxLength={2000}
          hideUpdateButton={canHideSaveButton()}
          updateNote={(note: string) => {
            setNoteModal({
              ...noteModal,
              open: false,
            });
            updateVisitLevelNote(
              noteModal.activitySheetId,
              noteModal.progressNotesId,
              noteModal.visitId,
              note,
            );
          }}
        />
      )}

      {showSaveAndSubmit && (
        <Box className="mt-sm">
          <MediumTypography
            className="mb-md"
            labelid="reviewActvivitySheetAttestationDesc"
            defaultlabel="This record was submitted with an attestation confirming that the information is accurate, complete, and true to the best of their knowledge at the time of submission"
          />

          <Box className="flex__ mb-xlg" justifyContent={'end'}>
            <Box
              className="ml-xs"
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <InfoIcon style={{ cursor: 'pointer' }} />
              <Popover
                id="mouse-over-popover"
                sx={{
                  pointerEvents: 'none',
                }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
              >
                <MediumTypography
                  className="p-sm"
                  labelid="billingSubmissionInfo"
                  defaultlabel="Once you click <b>Save & Submit</b>, the system will transmit all the Approved Visits to ThomBiller (TBR)"
                />
              </Popover>
            </Box>
            <ButtonComponent
              className="btn-primary btn-submit ml-md"
              variantType="contained"
              type="submit"
              labelId={'clientpage.submit'}
              defaultLabelId="Save & Submit"
              onClick={() => {
                if (context?.submitReviewedBillableHours) {
                  const allVisitsReviewed = isAllActivitiesVisitsReviewed();
                  const allDaysReviewed = isAllDayLevelReviewed();

                  if (allVisitsReviewed && allDaysReviewed) {
                    setShowConfirmationPopup(true);
                  } else if (!allDaysReviewed) {
                    setSuccessOrError('error');
                    setToastrId('daysNotReviewedMessage');
                    setToastrDefaultMessage(
                      'One or more days have not been reviewed for submission.',
                    );
                  } else if (!allVisitsReviewed) {
                    setSuccessOrError('error');
                    setToastrId('visitsNotReviewedMessage');
                    setToastrDefaultMessage(
                      'One or more visits have not been reviewed for submission.',
                    );
                  }
                }
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default BillableHoursView;
